import { useState, useContext } from "react";
import { CopyToClipboardButton, Modal, ModalContext, toast } from "dash-ui";
import { useGenerateNewApiTokenMutation } from "gql/generated.graphql";

import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

interface IProps {
  refetchAppInfo: () => Promise<void>;
  hasToken: boolean | null;
}

const ConfirmTokenGenerationModal = ({ refetchAppInfo, hasToken }: IProps) => {
  const { t } = useTranslation(["pages"]);
  const theme = useTheme();

  const { toggleModal, isOpenCurrent } = useContext(ModalContext);
  const [generateNewToken, { loading }] = useGenerateNewApiTokenMutation();
  const [token, setToken] = useState<string | null>(null);

  const isOpen = isOpenCurrent["GenerateNewApiToken"];

  const handleGenerateToken = async () => {
    try {
      const { data } = await generateNewToken();
      const generatedToken = data?.generateNewApiToken?.token;
      if (generatedToken) {
        setToken(generatedToken);
        toast.success(t("pages:profilePage:api:tokenGeneratedSuccess"));
        refetchAppInfo();
      }
    } catch (error) {
      toast.error(t("pages:profilePage:api:tokenGeneratedError"));
    }
  };

  const closeModal = () => {
    toggleModal(false, "GenerateNewApiToken");
    setToken(null);
  };

  const renderGeneratedTokenMessage = () => (
    <div className="mt-4" style={{ color: theme.colors.n80 }}>
      <p>{t("pages:profilePage:api:newToken")}</p>
      <div className="mb-4 flex items-center">
        <p className="break-all">
          <b>{token}</b>
        </p>
        <CopyToClipboardButton
          successMessage="Token copied!"
          textToCopy={token || ""}
        />
      </div>
      <p className="mb-6">{t("pages:profilePage:api:newTokenStoreSafely")}</p>
    </div>
  );

  const renderTokenConfirmationMessage = () => (
    <p className="mb-6 mt-4" style={{ color: theme.colors.n80 }}>
      Are you sure you want to generate a new token?
      <br /> The current one <strong>will be invalidated</strong>.
    </p>
  );

  const renderFirstTokenGenerationMessage = () => (
    <p className="mb-6 mt-4" style={{ color: theme.colors.n80 }}>
      <strong>Important!</strong> Once you generate a token, it will be
      displayed here.
      <br />
      <br />
      You <strong>need to copy</strong> it and store it in a safe place. If you
      lose it, you will need to generate a new one, and the old one{" "}
      <strong>will be invalidated</strong>.
    </p>
  );

  const determineMessageToRender = () => {
    if (token) {
      return renderGeneratedTokenMessage();
    }
    if (hasToken) {
      return renderTokenConfirmationMessage();
    }
    return renderFirstTokenGenerationMessage();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title="Generate New API Token"
      submitButton={{
        title: token ? "Close" : "Generate New Token",
        disabled: loading,
        onClick: token ? closeModal : handleGenerateToken,
      }}
      {...(!token && {
        cancelButton: {
          title: "Cancel",
          onClick: closeModal,
        },
      })}
    >
      {determineMessageToRender()}
    </Modal>
  );
};

export default ConfirmTokenGenerationModal;
